import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const plans = [
  {
    name: '10/90',
    label: 'Investor Friendly',
    color: '#22c55e',
    upfront: 10,
    description: 'Minimum capital outlay. 10% now, 90% on handover. Best for investors with limited liquidity.',
    pros: ['Lowest initial capital', 'Maximum leverage', 'Ideal for flipping before handover'],
    cons: ['Higher risk if market drops', 'Large balloon payment at handover', 'Requires mortgage pre-approval'],
    cashFlow: [10, 0, 0, 0, 90],
    bestFor: 'Short-term investors & flippers'
  },
  {
    name: '20/80',
    label: 'Most Popular',
    color: '#00D4FF',
    upfront: 20,
    description: 'Balanced approach. 20% now, 80% on handover. Most common plan offered by UAE developers.',
    pros: ['Moderate capital requirement', 'Widely available', 'Good for end-users & investors'],
    cons: ['Still large handover payment', 'Requires mortgage planning', 'Less leverage than 10/90'],
    cashFlow: [20, 0, 0, 0, 80],
    bestFor: 'End-users & medium-term investors'
  },
  {
    name: '50/50',
    label: 'Conservative',
    color: '#f59e0b',
    upfront: 50,
    description: 'Equal split during construction. Reduces handover payment significantly.',
    pros: ['Lower handover payment', 'Builds equity during construction', 'Easier mortgage at handover'],
    cons: ['Higher upfront capital', 'Less leverage', 'Opportunity cost of capital'],
    cashFlow: [10, 10, 10, 10, 60],
    bestFor: 'Conservative investors & end-users'
  }
];

const PaymentPlanComparison = () => {
  const [selected, setSelected] = useState('20/80');
  const [price, setPrice] = useState(1500000);
  const sel = plans?.find(p => p?.name === selected);

  return (
    <div className="rounded-2xl p-6" style={{ background: 'rgba(10,22,40,0.9)', border: '1px solid rgba(0,212,255,0.2)' }}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)' }}>
          <Icon name="BarChart2" size={20} color="#00D4FF" />
        </div>
        <div>
          <h3 className="text-lg font-bold" style={{ color: '#E8F4FF' }}>Payment Plan Comparison</h3>
          <p className="text-xs" style={{ color: '#6B9EC4' }}>Compare cash flow implications across UAE payment structures</p>
        </div>
      </div>
      <div className="mb-4">
        <label className="text-xs font-medium mb-1 block" style={{ color: '#6B9EC4' }}>Property Price (AED)</label>
        <input type="number" value={price} onChange={e => setPrice(+e?.target?.value)}
          className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
          style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.2)', color: '#E8F4FF' }} />
      </div>
      <div className="grid grid-cols-3 gap-3 mb-6">
        {plans?.map(plan => (
          <button key={plan?.name} onClick={() => setSelected(plan?.name)}
            className="p-3 rounded-xl text-center transition-all"
            style={{ background: selected === plan?.name ? `rgba(${plan?.color === '#22c55e' ? '34,197,94' : plan?.color === '#00D4FF' ? '0,212,255' : '245,158,11'},0.15)` : 'rgba(0,212,255,0.04)', border: `1px solid ${selected === plan?.name ? plan?.color : 'rgba(0,212,255,0.12)'}` }}>
            <div className="text-lg font-bold" style={{ color: plan?.color }}>{plan?.name}</div>
            <div className="text-xs" style={{ color: '#6B9EC4' }}>{plan?.label}</div>
          </button>
        ))}
      </div>
      {sel && (
        <div>
          <p className="text-sm mb-4" style={{ color: '#6B9EC4' }}>{sel?.description}</p>

          <div className="mb-4">
            <div className="text-xs font-semibold mb-2" style={{ color: '#6B9EC4' }}>CASH FLOW BREAKDOWN</div>
            <div className="flex gap-1 h-16 items-end">
              {sel?.cashFlow?.map((pct, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs" style={{ color: sel?.color }}>{pct}%</span>
                  <div className="w-full rounded-t" style={{ height: `${Math.max(pct, 4) * 0.6}px`, background: pct > 0 ? sel?.color : 'rgba(255,255,255,0.05)', minHeight: 4 }} />
                  <span className="text-xs" style={{ color: '#6B9EC4' }}>Yr{i + 1}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="p-3 rounded-xl mb-4" style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)' }}>
            <div className="text-xs" style={{ color: '#6B9EC4' }}>Initial Payment Required</div>
            <div className="text-xl font-bold" style={{ color: sel?.color }}>AED {Math.round(price * sel?.upfront / 100)?.toLocaleString()}</div>
            <div className="text-xs" style={{ color: '#6B9EC4' }}>({sel?.upfront}% of AED {price?.toLocaleString()})</div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-xs font-semibold mb-2" style={{ color: '#22c55e' }}>✓ Advantages</div>
              {sel?.pros?.map((p, i) => <div key={i} className="text-xs mb-1" style={{ color: '#6B9EC4' }}>• {p}</div>)}
            </div>
            <div>
              <div className="text-xs font-semibold mb-2" style={{ color: '#ef4444' }}>✗ Disadvantages</div>
              {sel?.cons?.map((c, i) => <div key={i} className="text-xs mb-1" style={{ color: '#6B9EC4' }}>• {c}</div>)}
            </div>
          </div>

          <div className="mt-3 p-2 rounded-lg text-xs" style={{ background: 'rgba(0,212,255,0.06)', color: '#00D4FF' }}>
            Best for: {sel?.bestFor}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentPlanComparison;
